import React, { useState } from 'react';
import { supabase } from '../../supabase';
import { USER_PROFILE_IMAGE } from '../../constants';
import { UserProfile, CompanyInfo } from '../../types';
import { Mail, Lock, LogIn, Loader2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ErpLoginProps {
  onLogin: (user: UserProfile) => void;
  company: CompanyInfo;
}

const ErpLogin: React.FC<ErpLoginProps> = ({ onLogin, company }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;

      let profileData = null;
      // Retry logic for profile generation (matching original logic)
      for (let i = 0; i < 5; i++) {
        const { data, error: pError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', authData.user.id)
          .single();
        
        if (data) {
          profileData = data;
          break;
        }
        await new Promise(r => setTimeout(r, 800));
      }

      if (!profileData) {
        throw new Error('Perfil não encontrado. Tente novamente.');
      }

      onLogin({
        id: profileData.id,
        name: profileData.name || 'Usuário Magenta',
        email: profileData.email || authData.user.email!,
        role: profileData.role as 'admin' | 'staff' | 'finance',
        avatarUrl: profileData.avatar_url || USER_PROFILE_IMAGE,
        isFirstLogin: profileData.is_first_login,
      });
    } catch (err: any) {
      setError(err.message === 'Invalid login credentials' ? 'E-mail ou senha incorretos.' : err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#fcf8fa] p-4 font-display">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-[400px]"
      >
        <div className="bg-white rounded-2xl shadow-md border border-[#f4e6ee] overflow-hidden">
          {/* Header Area */}
          <div className="p-8 pb-4 flex flex-col items-center">
            <div className="bg-[#fcebf5] p-3 rounded-2xl mb-6 size-16 flex items-center justify-center border border-[#8a0049]/10">
              {company.logoUrl ? (
                <img src={company.logoUrl} className="w-full h-full object-contain" alt="Logo" />
              ) : (
                <div className="bg-[#8a0049] p-2 rounded-lg">
                  <LogIn className="text-white size-6" />
                </div>
              )}
            </div>
            <h1 className="text-2xl font-bold text-[#1d0c15] tracking-tight text-center">Acesso ao Sistema</h1>
            <p className="text-[#6b5863] text-sm mt-1 text-center">Bem-vindo ao {company.name}</p>
          </div>
          
          <form onSubmit={handleSubmit} className="p-8 pt-4 flex flex-col gap-5">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-[#6b5863] ml-1">E-mail</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-[#6b5863]/50" />
                <input 
                  type="email" 
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full h-12 pl-11 pr-4 rounded-xl bg-gray-50 border border-gray-200 focus:border-[#8a0049]/30 focus:bg-white focus:ring-4 focus:ring-[#8a0049]/5 outline-none transition-all text-sm text-[#1d0c15]"
                  placeholder="seu@email.com"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-[#6b5863] ml-1">Senha</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-[#6b5863]/50" />
                <input 
                  type="password" 
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full h-12 pl-11 pr-4 rounded-xl bg-gray-50 border border-gray-200 focus:border-[#8a0049]/30 focus:bg-white focus:ring-4 focus:ring-[#8a0049]/5 outline-none transition-all text-sm text-[#1d0c15]"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-2 bg-red-50 text-red-600 p-3 rounded-lg text-xs font-medium border border-red-100"
                >
                  <AlertCircle size={14} />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <button 
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-[#8a0049] hover:bg-[#6e003a] text-white rounded-xl font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-70 mt-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin size-4" />
                  Autenticando...
                </>
              ) : (
                'Entrar no Painel'
              )}
            </button>
          </form>

          <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-center">
            <p className="text-[10px] text-[#6b5863] font-medium uppercase tracking-widest text-center">
              Magenta ERP • Tecnologia e Gestão
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ErpLogin;
